import { useRef, useState, useCallback, useEffect, RefObject } from 'react';
import { WebAudioService } from '../services/audio/webAudioService';
import { useRoomSocket } from './useRoomSocket';
import { useRoomConfig } from './useRoomConfig';
import { useRoomState } from './useRoomState';
import type { VolumeSettings } from './useMediaVolumeManager';
import { userVolumeToGain } from '../services/audio/audioVolumeAdapter';
import {
  generateCommandId,
  getSoundEffectsByGame,
  getSoundEffectPath,
  createLogger,
  findGameName,
} from '@airhead-games/shared/utilities';
import {
  MediaActionType,
  MediaType,
  SenderRoleType,
  MediaReadyEvent,
} from '@airhead-games/shared/types';

const logger = createLogger('useMediaSfxManager');

// Keep track of dynamically created audio elements
interface AudioNode {
  element: HTMLAudioElement;
  source: MediaElementAudioSourceNode | null;
  inUse: boolean;
}

export function useMediaSfxManager(
  sfxAudioRef: RefObject<HTMLAudioElement | null>,
  currentVolumes: VolumeSettings,
  webAudio?: WebAudioService
) {
  const [isSfxPlaying, setIsSfxPlaying] = useState(false);
  const cachedSoundUrls = useRef<Record<string, string>>({}); // Cache for preloaded sound effects URLs
  const audioPool = useRef<AudioNode[]>([]); // Pool for dynamically created audio elements
  const activeNodesCount = useRef(0); // Counter for active audio nodes
  const pendingRequests = useRef<Record<string, (url: string) => void>>({});

  // Get the sendCommand function from the room context and roomCode from the selector
  const { sendCommand } = useRoomSocket();
  const { roomCode } = useRoomConfig();
  const { isReplayingEvents } = useRoomState();

  // Requests a sound effect URL from backend if not already cached
  const requestSoundEffect = useCallback(
    async (sfxId: string): Promise<string> => {
      // 1. Check cache
      if (cachedSoundUrls.current[sfxId]) {
        return cachedSoundUrls.current[sfxId];
      }

      // 2. Validate roomCode
      if (!roomCode) {
        throw new Error('Cannot request SFX: roomCode is missing');
      }

      // 3. Issue backend request for SFX
      sendCommand({
        id: generateCommandId(),
        type: 'MediaElement',
        action: MediaActionType.REQUEST,
        mediaType: MediaType.SFX,
        senderRole: SenderRoleType.HOST,
        clientTimestamp: Date.now(),
        roomCode,
        content: sfxId,
        sfxUrl: getSoundEffectPath(sfxId),
      });

      // 4. Wait for response (signed URL will be provided by updateSfxWithSignedUrl)
      return new Promise<string>((resolve, reject) => {
        const timeoutId = setTimeout(() => {
          delete pendingRequests.current[sfxId];
          reject(new Error('SFX request timeout'));
        }, 15000);

        pendingRequests.current[sfxId] = (url: string) => {
          clearTimeout(timeoutId);
          resolve(url);
        };
      });
    },
    [sendCommand, roomCode]
  );

  // Creates a new audio node or reuses an available one from the pool
  const getAudioNode = useCallback((): AudioNode => {
    // First check if any existing nodes are free
    const availableNode = audioPool.current.find((node) => !node.inUse);

    if (availableNode) {
      availableNode.inUse = true;
      return availableNode;
    }

    // If no free nodes, create a new one
    const audioElement = document.createElement('audio');
    audioElement.crossOrigin = 'anonymous';

    // Connect to Web Audio if available
    let source: MediaElementAudioSourceNode | null = null;
    if (webAudio) {
      source = webAudio.connectSource(audioElement, 'sfx');
    }

    // Create new node
    const newNode: AudioNode = {
      element: audioElement,
      source,
      inUse: true,
    };

    // Add to pool
    audioPool.current.push(newNode);

    return newNode;
  }, [webAudio]);

  // Release an audio node back to the pool
  const releaseAudioNode = useCallback((node: AudioNode) => {
    // Reset the audio element
    node.element.pause();
    node.element.currentTime = 0;

    // Mark as available
    node.inUse = false;

    // Update active count
    activeNodesCount.current--;

    // Update playing state if no active nodes
    if (activeNodesCount.current === 0) {
      setIsSfxPlaying(false);
    }
  }, []);

  // Play sound effect - requests from backend if not cached
  const playSoundEffect = useCallback(
    async (sfxId: string): Promise<void> => {
      try {
        // Skip playback during event replay
        if (isReplayingEvents) {
          logger.debug(`Skipping SFX ${sfxId} during event replay`);
          return;
        }

        // Get the sound URL (from cache or backend)
        const soundUrl = await requestSoundEffect(sfxId);

        // Debug log
        logger.debug(`Playing ${sfxId}...`);

        // Get an audio node
        const audioNode = getAudioNode();
        audioNode.element.src = soundUrl;
        audioNode.element.currentTime = 0;

        // Set SFX element volume to full when WebAudio is active
        // Fallback to user-configured volume when WebAudio is unavailable
        audioNode.element.volume = audioNode.source
          ? 1.0
          : userVolumeToGain('sfxVolume', currentVolumes.sfxVolume);

        // Update playing state
        setIsSfxPlaying(true);
        activeNodesCount.current++;

        try {
          // Play the audio
          await audioNode.element.play();

          // Wait until playback ends (or the safety timeout fires)
          await new Promise<void>((resolve) => {
            audioNode.element.onended = () => {
              logger.debug(`${sfxId} finished playing`);
              audioNode.element.onended = null;
              releaseAudioNode(audioNode);
              resolve();
            };

            const safetyTimeout = setTimeout(() => {
              if (audioNode.inUse) {
                logger.debug(`Safety cleanup for ${sfxId}`);
                releaseAudioNode(audioNode);
                resolve();
              }
            }, 10000);

            audioNode.element.addEventListener(
              'ended',
              () => {
                clearTimeout(safetyTimeout);
              },
              { once: true }
            );
          });
        } catch (error) {
          logger.error(`Playback error for ${sfxId}:`, error);
          releaseAudioNode(audioNode);
        }
      } catch (error) {
        logger.error(`Failed to play ${sfxId}:`, error);
      }
    },
    [
      getAudioNode,
      releaseAudioNode,
      requestSoundEffect,
      isReplayingEvents,
      currentVolumes,
    ]
  );

  // Play synthetic note using the WebAudioService
  const playNote = useCallback(
    (semitones: number = 0): void => {
      if (!webAudio) {
        logger.warn('WebAudio service not available for synthetic notes');
        return;
      }

      webAudio.playNote(semitones);
    },
    [webAudio]
  );

  // Stop all sound effects
  const stopSfxPlayback = useCallback(() => {
    // Stop all pool audio
    audioPool.current.forEach((node) => {
      if (node.inUse) {
        node.element.pause();
        node.element.currentTime = 0;
        node.inUse = false;
      }
    });

    // Also stop the main audio element
    if (sfxAudioRef.current) {
      sfxAudioRef.current.pause();
      sfxAudioRef.current.currentTime = 0;
    }

    activeNodesCount.current = 0;
    setIsSfxPlaying(false);
  }, [sfxAudioRef]);

  // Flush all SFX manager state for room transitions
  const flushSfxManager = useCallback(() => {
    logger.debug('Flushing SFX manager state...');
    stopSfxPlayback();
    // Additional cleanup for room transitions
    audioPool.current.forEach((node) => {
      node.element.src = '';
      node.element.removeEventListener('ended', () => {});
      node.inUse = false;
    });
    activeNodesCount.current = 0;
    setIsSfxPlaying(false);
    logger.debug('SFX manager flushed');
  }, [stopSfxPlayback]);

  // Clean up all audio resources when the component unmounts
  useEffect(() => {
    return () => {
      // Disconnect all audio nodes from Web Audio
      audioPool.current.forEach((node) => {
        if (webAudio) {
          webAudio.disconnectSource(node.element);
        }
        node.element.pause();
        node.element.src = '';
      });
      audioPool.current = [];
    };
  }, [webAudio]);

  // Load all sound effects for a specific game
  const requestGameSfx = useCallback(
    async (gameId: string): Promise<void> => {
      const gameSfx = getSoundEffectsByGame(gameId);

      // Filter to only uncached SFX
      const uncachedSfx = gameSfx.filter(
        (sfx) => !cachedSoundUrls.current[sfx.id]
      );

      if (uncachedSfx.length === 0) {
        logger.debug(
          `All SFX already loaded for ${findGameName(gameId)}, skipping requestGameSfx`
        );
        return;
      }

      logger.debug(
        `Loading ${uncachedSfx.length} SFX for ${findGameName(gameId)}...`
      );

      // Validate roomCode
      if (!roomCode) {
        throw new Error('Cannot request SFX: roomCode is missing');
      }

      // Build items array for batched request (only uncached)
      const items = uncachedSfx.map((sfx) => ({
        mediaType: MediaType.SFX,
        content: sfx.id,
        sfxUrl: getSoundEffectPath(sfx.id),
      }));

      // Set up pending requests for uncached SFX only
      const loadPromises = uncachedSfx.map((sfx) => {
        return new Promise<void>((resolve, reject) => {
          const timeoutId = setTimeout(() => {
            delete pendingRequests.current[sfx.id];
            reject(new Error(`SFX request timeout for ${sfx.id}`));
          }, 15000);

          pendingRequests.current[sfx.id] = () => {
            clearTimeout(timeoutId);
            resolve();
          };
        });
      });

      // Send single batched request
      sendCommand({
        id: generateCommandId(),
        type: 'MediaElement',
        action: MediaActionType.REQUEST,
        mediaType: MediaType.SFX,
        senderRole: SenderRoleType.HOST,
        clientTimestamp: Date.now(),
        roomCode,
        content: uncachedSfx.map((sfx) => sfx.id).join(', '),
        items,
      });

      // Wait for all responses
      await Promise.allSettled(loadPromises);
      logger.debug(
        `${uncachedSfx.length} SFX loaded for ${findGameName(gameId)}`
      );
    },
    [sendCommand, roomCode]
  );

  // Update SFX with signed URL from centralized media manager
  const updateSfxWithSignedUrl = useCallback((event: MediaReadyEvent) => {
    const { content: sfxId, sfxUrl } = event;

    if (!sfxId || !sfxUrl) {
      logger.warn('updateSfxWithSignedUrl called without sfxId or sfxUrl');
      return;
    }

    // Cache the URL
    cachedSoundUrls.current[sfxId] = sfxUrl;

    // Resolve any pending requests for this SFX
    const pendingCallback = pendingRequests.current[sfxId];
    if (pendingCallback) {
      pendingCallback(sfxUrl);
      delete pendingRequests.current[sfxId];
    }

    logger.debug(`SFX loaded: ${sfxId}`);
  }, []);

  return {
    isSfxPlaying,
    requestSoundEffect,
    requestGameSfx,
    playSoundEffect,
    playNote,
    stopSfxPlayback,
    updateSfxWithSignedUrl,
    flushSfxManager,
  };
}
